<template>
  <section ref="heroRef" class="hero" :class="{ 'hero--hover': isHovering }" @click="onHeroClick">
    <div class="hero__cursor-glow" aria-hidden="true"></div>

    <div class="hero__bg" aria-hidden="true">
      <HeroParticles ref="particlesRef" />
      <div class="hero__bg-gradient"></div>
      <div class="hero__bg-mesh"></div>
      <div class="hero__bg-hex"></div>
      <div class="hero__bg-grid"></div>
      <div class="hero__glow hero__glow--1"></div>
      <div class="hero__glow hero__glow--2"></div>
      <div class="hero__glow hero__glow--3"></div>
      <div class="hero__bg-line hero__bg-line--1"></div>
      <div class="hero__bg-line hero__bg-line--2"></div>
    </div>

    <div class="hero__layout">
      <div class="hero__content">
        <span class="hero__eyebrow reveal">
          <span class="hero__eyebrow-dot"></span>
          Hello
        </span>

        <h1 class="hero__title reveal reveal-delay-1">
          <span class="hero__line">i'm {{ SITE.name }}</span>
          <span class="hero__line hero__line--role">
            a
            <span class="hero__role-text">{{ displayText }}</span>
            <span class="hero__cursor">|</span>
          </span>
        </h1>

        <p class="hero__desc reveal reveal-delay-3">
          A personal portfolio is a curated collection of work, achievements,
          and skills that showcases professional expertise and creative identity.
        </p>

        <button type="button" class="hero__cta reveal reveal-delay-4" @click="goTo('portfolio')">
          <span>View Portfolio</span>
          <v-icon size="18">mdi-arrow-right</v-icon>
        </button>
      </div>

      <div class="hero__visual reveal reveal-delay-2">
        <div class="hero__bg-text" aria-hidden="true">{{ SITE.tagline }}</div>
        <div class="hero__ring-glow" aria-hidden="true"></div>
        <div class="hero__shapes" aria-hidden="true">
          <span class="hero__shape hero__shape--1"></span>
          <span class="hero__shape hero__shape--2"></span>
          <span class="hero__shape hero__shape--3"></span>
        </div>
        <img
          :src="SITE.profilePhoto"
          :alt="`${SITE.name} profile photo`"
          class="hero__photo"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { SITE, TYPEWRITER_WORDS } from "@/constants/site";
import { useNavigation } from "@/composables/useNavigation";
import { useTypewriter } from "@/composables/useTypewriter";
import { useScrollReveal } from "@/composables/useScrollReveal";
import { useHeroMouse } from "@/composables/useHeroMouse";
import HeroParticles from "@/components/effects/HeroParticles.vue";

const heroRef = ref(null);
const particlesRef = ref(null);
const { navigateToSection } = useNavigation();
const { displayText } = useTypewriter(TYPEWRITER_WORDS, { pauseDuration: 2200 });
const { isHovering } = useHeroMouse(heroRef);

useScrollReveal();

onMounted(() => {
  setTimeout(() => {
    document.querySelectorAll(".hero .reveal").forEach((el) => el.classList.add("is-visible"));
  }, 200);
});

const goTo = (id) => navigateToSection(id);

const onHeroClick = (event) => {
  if (event.target.closest("button, a")) return;

  const hero = heroRef.value;
  if (!hero || !particlesRef.value) return;

  const rect = hero.getBoundingClientRect();
  particlesRef.value.addRipple(
    event.clientX - rect.left,
    event.clientY - rect.top
  );
};
</script>

<style scoped>
.hero {
  --mx: 0.5;
  --my: 0.5;
  --blue: var(--color-accent);
  --blue-glow: var(--color-accent-glow);

  min-height: 100vh;
  padding-top: var(--header-height);
  position: relative;
  overflow: hidden;
  cursor: crosshair;
}

/* Mouse-follow spotlight */
.hero__cursor-glow {
  position: absolute;
  left: var(--cursor-x, 50%);
  top: var(--cursor-y, 50%);
  width: 320px;
  height: 320px;
  margin: -160px 0 0 -160px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(4, 33, 59, 0.12) 0%, transparent 65%);
  pointer-events: none;
  z-index: 0;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.hero--hover .hero__cursor-glow {
  opacity: 1;
}

/* ── Neon background ── */
.hero__bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  transform: translate(
    calc((var(--mx) - 0.5) * -18px),
    calc((var(--my) - 0.5) * -12px)
  );
  transition: transform 0.2s ease-out;
}

.hero__bg :deep(.hero-particles-canvas) {
  z-index: 2;
}

.hero__bg-gradient {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(135deg, rgba(4, 33, 59, 0.1) 0%, transparent 45%),
    linear-gradient(225deg, rgba(0, 80, 160, 0.08) 0%, transparent 40%),
    radial-gradient(ellipse at 20% 80%, rgba(4, 33, 59, 0.05) 0%, transparent 50%);
}

.hero__bg-mesh {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 25% 25%, rgba(4, 33, 59, 0.06) 0%, transparent 8%),
    radial-gradient(circle at 75% 60%, rgba(4, 33, 59, 0.04) 0%, transparent 6%);
  animation: meshPulse 8s ease-in-out infinite alternate;
}

@keyframes meshPulse {
  0% { opacity: 0.6; }
  100% { opacity: 1; }
}

.hero__bg-hex {
  position: absolute;
  top: 0;
  left: 0;
  width: 55%;
  height: 100%;
  opacity: 0.35;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='100' viewBox='0 0 56 100'%3E%3Cpath d='M28 0 L56 16 L56 50 L28 66 L0 50 L0 16 Z M28 34 L56 50 L56 84 L28 100 L0 84 L0 50 Z' fill='none' stroke='%2304213b' stroke-width='0.6' opacity='0.4'/%3E%3C/svg%3E");
  background-size: 56px 100px;
  mask-image: linear-gradient(to right, black 30%, transparent 100%);
  -webkit-mask-image: linear-gradient(to right, black 30%, transparent 100%);
}

.hero__bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(4, 33, 59, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(4, 33, 59, 0.04) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse at 30% 50%, black 20%, transparent 70%);
  -webkit-mask-image: radial-gradient(ellipse at 30% 50%, black 20%, transparent 70%);
}

.hero__glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  animation: glowFloat 6s ease-in-out infinite alternate;
}

.hero__glow--1 {
  width: 320px;
  height: 320px;
  background: rgba(4, 33, 59, 0.15);
  top: -80px;
  left: -60px;
}

.hero__glow--2 {
  width: 240px;
  height: 240px;
  background: rgba(4, 33, 59, 0.08);
  bottom: 10%;
  left: 25%;
  animation-delay: -2s;
}

.hero__glow--3 {
  width: 200px;
  height: 200px;
  background: rgba(3, 27, 49, 0.12);
  top: 40%;
  right: 15%;
  animation-delay: -4s;
}

@keyframes glowFloat {
  0% { transform: translate(0, 0) scale(1); opacity: 0.7; }
  100% { transform: translate(20px, -15px) scale(1.1); opacity: 1; }
}

.hero__bg-line {
  position: absolute;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(4, 33, 59, 0.5), transparent);
  box-shadow: 0 0 12px rgba(4, 33, 59, 0.4);
}

.hero__bg-line--1 {
  width: 40%;
  top: 22%;
  left: 0;
  transform: rotate(-8deg);
}

.hero__bg-line--2 {
  width: 30%;
  bottom: 30%;
  left: 5%;
  transform: rotate(5deg);
  opacity: 0.6;
}

/* ── Layout ── */
.hero__layout {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  min-height: calc(100vh - var(--header-height));
  padding-left: clamp(1.25rem, 4vw, 3.5rem);
  padding-right: clamp(1rem, 2vw, 2rem);
}

.hero__content {
  flex: 0 0 auto;
  max-width: 580px;
  padding: 2rem 0 3rem;
  transform: translate(
    calc((var(--mx) - 0.5) * -12px),
    calc((var(--my) - 0.5) * -8px)
  );
  transition: transform 0.2s ease-out;
}

@media (min-width: 960px) {
  .hero__content {
    width: 48%;
    max-width: 620px;
    padding: 0;
  }
}

.hero__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #ffffff;
  margin-bottom: 1.25rem;
  text-shadow: 0 0 16px rgba(4, 33, 59, 0.8);
}

.hero__eyebrow-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--blue);
  box-shadow: 0 0 10px var(--blue), 0 0 20px rgba(4, 33, 59, 0.8);
  animation: dotPulse 2s ease-in-out infinite;
}

@keyframes dotPulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(0.85); }
}

.hero__title {
  font-size: clamp(2rem, 4.5vw, 3.75rem);
  font-weight: 700;
  line-height: 1.12;
  letter-spacing: -0.02em;
  margin: 0 0 1.5rem;
  text-transform: lowercase;
}

.hero__line {
  display: block;
  white-space: nowrap;
}

.hero__line--role {
  font-size: clamp(1.625rem, 3.5vw, 2.625rem);
  font-weight: 700;
  margin-top: 0.375rem;
}

.hero__role-text {
  color: #ffffff;
  text-shadow: 0 0 24px rgba(4, 33, 59, 0.9), 0 0 48px rgba(4, 33, 59, 0.5);
}

.hero__cursor {
  color: #ffffff;
  animation: blink 1s step-end infinite;
  text-shadow: 0 0 12px rgba(4, 33, 59, 0.9);
}

@keyframes blink {
  50% { opacity: 0; }
}

.hero__desc {
  font-size: clamp(0.9375rem, 1.5vw, 1.0625rem);
  line-height: 1.8;
  color: var(--color-text-muted);
  max-width: 460px;
  margin: 0 0 2.25rem;
}

.hero__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  height: 54px;
  padding: 0 2rem;
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #fff;
  background: var(--blue);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: var(--radius-full);
  cursor: pointer;
  box-shadow:
    0 0 20px rgba(4, 33, 59, 0.5),
    0 4px 24px rgba(3, 27, 49, 0.4);
  transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
}

.hero__cta:hover {
  background: var(--color-accent-hover);
  transform: translateY(-3px) scale(1.02);
  box-shadow:
    0 0 30px rgba(4, 33, 59, 0.6),
    0 8px 32px rgba(3, 27, 49, 0.5);
}

/* ── Portrait — right edge ── */
.hero__visual {
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 1;
  width: min(50vw, 580px);
  height: calc(100vh - var(--header-height));
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  pointer-events: none;
  transform: translate(
    calc((var(--mx) - 0.5) * -24px),
    calc((var(--my) - 0.5) * -16px)
  );
  transition: transform 0.2s ease-out;
}

@media (max-width: 959px) {
  .hero__visual {
    position: relative;
    width: 100%;
    height: auto;
    min-height: 340px;
    margin-top: 1.5rem;
    justify-content: center;
    transform: none;
  }

  .hero__content {
    transform: none;
  }

  .hero__bg {
    transform: none;
  }
}

.hero__bg-text {
  position: absolute;
  font-size: clamp(2.5rem, 7vw, 5.5rem);
  font-weight: 800;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.03);
  white-space: nowrap;
  top: 42%;
  left: 32%;
  transform: translate(-50%, -50%)
    translate(calc((var(--mx) - 0.5) * 28px), calc((var(--my) - 0.5) * 18px));
  z-index: 0;
  transition: transform 0.2s ease-out;
}

.hero__ring-glow {
  position: absolute;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  border: 1px solid rgba(4, 33, 59, 0.3);
  box-shadow: 0 0 40px rgba(4, 33, 59, 0.15), inset 0 0 30px rgba(4, 33, 59, 0.05);
  top: 30%;
  left: 12%;
  z-index: 0;
  animation: ringPulse 4s ease-in-out infinite;
  transform: translate(
    calc((var(--mx) - 0.5) * 20px),
    calc((var(--my) - 0.5) * 14px)
  );
  transition: transform 0.2s ease-out;
}

@keyframes ringPulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

.hero__shapes {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.hero__shape {
  position: absolute;
  border-radius: 50%;
  transition: transform 0.2s ease-out;
}

.hero--hover .hero__shape--1 {
  transform: translate(
    calc((var(--mx) - 0.5) * 30px),
    calc((var(--my) - 0.5) * 20px)
  );
}

.hero--hover .hero__shape--2 {
  transform: translate(
    calc((var(--mx) - 0.5) * -25px),
    calc((var(--my) - 0.5) * -18px)
  );
}

.hero--hover .hero__shape--3 {
  transform: translate(
    calc((var(--mx) - 0.5) * 18px),
    calc((var(--my) - 0.5) * -12px)
  );
}

.hero__shape--1 {
  width: 64px;
  height: 64px;
  background: var(--blue);
  top: 16%;
  right: 30%;
  box-shadow: 0 0 24px rgba(4, 33, 59, 0.6);
}

.hero__shape--2 {
  width: 96px;
  height: 96px;
  border: 2px solid var(--blue);
  bottom: 30%;
  left: 5%;
  box-shadow: 0 0 20px rgba(4, 33, 59, 0.3);
}

.hero__shape--3 {
  width: 48px;
  height: 48px;
  background: var(--blue);
  bottom: 16%;
  right: 38%;
  clip-path: inset(0 0 50% 0);
  border-radius: 48px 48px 0 0;
  box-shadow: 0 0 16px rgba(4, 33, 59, 0.5);
}

.hero__photo {
  position: relative;
  z-index: 2;
  height: calc(100vh - var(--header-height) - 16px);
  width: auto;
  max-width: 100%;
  object-fit: contain;
  object-position: bottom right;
  display: block;
  filter: drop-shadow(0 0 30px rgba(4, 33, 59, 0.1));
  transform: translate(
    calc((var(--mx) - 0.5) * -16px),
    calc((var(--my) - 0.5) * -10px)
  );
  transition: transform 0.2s ease-out, filter 0.3s ease;
}

.hero--hover .hero__photo {
  filter: drop-shadow(0 0 40px rgba(4, 33, 59, 0.2));
}

@media (max-width: 959px) {
  .hero__photo {
    height: auto;
    max-height: 400px;
    object-position: bottom center;
    transform: none;
  }

  .hero__line {
    white-space: normal;
  }

  .hero__ring-glow {
    display: none;
  }

  .hero__bg-text {
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
